import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SectionHeader } from "./SectionHeader";
import { DateRangeField, toIsoDate } from "./DateRangeField";
import { useT } from "@/lib/i18n";

export const ContactCTA = () => {
  const { t, lang } = useT();
  // The whole chalet is the only thing on offer, so there is no unit selector.
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    persons: "",
    message: "",
  });
  const [stay, setStay] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.ui.requiredError);
      return;
    }
    setSubmitting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${backendUrl}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          language: lang,
          check_in: stay?.from ? toIsoDate(stay.from) : "",
          check_out: stay?.to ? toIsoDate(stay.to) : "",
        }),
      });
      // The stay was taken between page load and submit.
      if (res.status === 409) {
        toast.error(t.contact.dates.unavailable);
        setStay(undefined);
        return;
      }
      if (!res.ok) throw new Error("Request failed");
      toast.success(t.ui.thankYou);
      setForm({ name: "", email: "", phone: "", persons: "", message: "" });
      setStay(undefined);
    } catch (err) {
      toast.error(t.ui.thankYouError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="section-y bg-secondary/40" aria-labelledby="kontakt-title">
      <div className="container-x">
        <SectionHeader kicker={t.contact.kicker} title={t.contact.title} subtitle={t.contact.subtitle} />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          <form onSubmit={onSubmit} className="lg:col-span-3 rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-soft" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">{t.contact.form.name}</Label>
                <Input id="name" data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder={t.contact.form.namePh} required />
              </div>
              <div>
                <Label htmlFor="email">{t.contact.form.email}</Label>
                <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder={t.contact.form.emailPh} required />
              </div>
              <div>
                <Label htmlFor="phone">{t.contact.form.phone}</Label>
                <Input id="phone" data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder={t.contact.form.phonePh} />
              </div>
              <div>
                <Label htmlFor="persons">{t.contact.form.persons}</Label>
                <Input id="persons" type="number" min="1" max="20" data-testid="contact-persons" value={form.persons} onChange={(e) => update("persons", e.target.value)} placeholder={t.contact.form.personsPh} />
              </div>
              <DateRangeField value={stay} onChange={setStay} />
            </div>
            <div className="mt-4">
              <Label htmlFor="message">{t.contact.form.message}</Label>
              <Textarea id="message" data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={4} placeholder={t.contact.form.messagePh} />
            </div>
            <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:justify-between">
              <p className="text-xs text-muted-foreground">{t.contact.form.gdprNote}</p>
              <Button type="submit" size="lg" className="h-12 rounded-xl px-6" disabled={submitting} data-testid="contact-submit">
                <span className="inline-flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  {submitting ? t.ui.submitting : t.ui.submitInquiry}
                </span>
              </Button>
            </div>
          </form>

          <aside className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="kicker">{t.contact.directTitle}</div>
              <div className="mt-4 space-y-4">
                <a href={t.brand.phoneHref} className="flex items-center gap-3 group" data-testid="contact-phone-link">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}>
                    <Phone className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs text-muted-foreground">{t.contact.addressLabels.phone}</div>
                    <div className="text-sm font-medium text-foreground group-hover:text-primary">{t.brand.phone}</div>
                  </div>
                </a>
                <a href={t.brand.emailHref} className="flex items-center gap-3 group" data-testid="contact-email-link">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}>
                    <Mail className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs text-muted-foreground">{t.contact.addressLabels.email}</div>
                    <div className="text-sm font-medium text-foreground group-hover:text-primary">{t.brand.email}</div>
                  </div>
                </a>
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg" style={{ background: "hsl(var(--pine))", color: "hsl(var(--primary-foreground))" }}>
                    <MapPin className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-xs text-muted-foreground">{t.contact.addressLabels.address}</div>
                    <address className="not-italic text-sm text-foreground leading-relaxed">
                      {t.brand.address.line1}<br />
                      {t.brand.address.city}
                    </address>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl p-6 shadow-soft text-primary-foreground" style={{ background: "hsl(var(--pine))" }}>
              <div className="kicker-on-dark">{t.contact.quickCallKicker}</div>
              <h3 className="mt-2 font-display text-xl font-semibold">{t.contact.quickCallTitle}</h3>
              <p className="mt-2 text-sm text-white/85">{t.contact.quickCallBody}</p>
              <Button asChild size="lg" variant="secondary" className="mt-4 h-11 rounded-xl bg-white text-primary hover:bg-white/90" data-testid="quick-call-cta">
                <a href={t.brand.phoneHref}>{t.ui.callNow}</a>
              </Button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};
